import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const SELECTOR =
    '[data-path-junction-map-picker]';

const junctionIcon = L.divIcon({
    className: '',
    html: `
        <div
            style="
                display:flex;
                align-items:center;
                justify-content:center;
                width:34px;
                height:34px;
                border-radius:9999px;
                border:3px solid white;
                background:#4f46e5;
                color:white;
                font-size:17px;
                box-shadow:0 6px 18px rgba(0,0,0,.35);
            "
        >
            ◆
        </div>
    `,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
});

function parseJsonArray(value) {
    try {
        const parsed = JSON.parse(
            value || '[]'
        );

        return Array.isArray(parsed)
            ? parsed
            : [];
    } catch {
        return [];
    }
}

function parseCoordinate(value) {
    const parsed =
        Number.parseFloat(value);

    return Number.isFinite(parsed)
        ? parsed
        : null;
}

function initializePicker(picker) {
    if (
        picker.dataset
            .pathJunctionMapReady
        === 'true'
    ) {
        return;
    }

    const form =
        picker.closest('form');

    const mapElement =
        picker.querySelector(
            '[data-path-junction-map]'
        );

    const status =
        picker.querySelector(
            '[data-path-junction-map-status]'
        );

    const latitudeInput =
        form?.querySelector(
            '[data-path-junction-latitude]'
        );

    const longitudeInput =
        form?.querySelector(
            '[data-path-junction-longitude]'
        );

    if (
        !form
        || !mapElement
        || !latitudeInput
        || !longitudeInput
    ) {
        return;
    }

    picker.dataset.pathJunctionMapReady =
        'true';

    const savedLatitude =
        parseCoordinate(
            latitudeInput.value
        );

    const savedLongitude =
        parseCoordinate(
            longitudeInput.value
        );

    const centerLatitude =
        parseCoordinate(
            picker.dataset.centerLatitude
        );

    const centerLongitude =
        parseCoordinate(
            picker.dataset.centerLongitude
        );

    const existingPaths =
        parseJsonArray(
            picker.dataset.existingPaths
        );

    const existingJunctions =
        parseJsonArray(
            picker.dataset.existingJunctions
        );

    const currentJunctionId =
        Number.parseInt(
            picker.dataset.currentJunctionId
            || '',
            10
        );

    const initialLatitude =
        savedLatitude
        ?? centerLatitude
        ?? 0;

    const initialLongitude =
        savedLongitude
        ?? centerLongitude
        ?? 0;

    const map = L.map(
        mapElement,
        {
            zoomControl: true,
            scrollWheelZoom: false,
        }
    ).setView(
        [
            initialLatitude,
            initialLongitude,
        ],
        savedLatitude !== null
        || centerLatitude !== null
            ? 17
            : 2
    );

    L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        {
            maxZoom: 20,
            attribution:
                '&copy; OpenStreetMap contributors',
        }
    ).addTo(map);

    const contextLayer =
        L.layerGroup().addTo(map);

    existingPaths.forEach(
        (existingPath) => {
            const pathPoints =
                Array.isArray(
                    existingPath.path_points
                )
                    ? existingPath.path_points
                        .map((point) => {
                            const latitude =
                                parseCoordinate(
                                    point?.latitude
                                );
                            const longitude =
                                parseCoordinate(
                                    point?.longitude
                                );

                            return (
                                latitude !== null
                                && longitude !== null
                            )
                                ? [
                                    latitude,
                                    longitude,
                                ]
                                : null;
                        })
                        .filter(Boolean)
                    : [];

            if (pathPoints.length < 2) {
                return;
            }

            L.polyline(
                pathPoints,
                {
                    color: '#64748b',
                    weight: 4,
                    opacity: 0.72,
                }
            )
                .addTo(contextLayer)
                .bindTooltip(
                    `${existingPath.name || 'Existing path'}${
                        existingPath.type
                            ? ` · ${existingPath.type}`
                            : ''
                    }`,
                    {
                        sticky: true,
                    }
                );
        }
    );

    existingJunctions.forEach(
        (junction) => {
            if (
                Number(junction.id)
                === currentJunctionId
            ) {
                return;
            }

            const latitude =
                parseCoordinate(
                    junction.latitude
                );
            const longitude =
                parseCoordinate(
                    junction.longitude
                );

            if (
                latitude === null
                || longitude === null
            ) {
                return;
            }

            L.circleMarker(
                [
                    latitude,
                    longitude,
                ],
                {
                    radius: 7,
                    color: '#ffffff',
                    weight: 2,
                    fillColor: '#4f46e5',
                    fillOpacity:
                        junction.is_active
                            ? 0.9
                            : 0.4,
                }
            )
                .addTo(contextLayer)
                .bindTooltip(
                    `${junction.name || 'Junction'}${
                        junction.type
                            ? ` · ${junction.type}`
                            : ''
                    }`,
                    {
                        direction: 'top',
                    }
                );
        }
    );

    let marker = null;
    let syncing = false;

    const setStatus = (
        latitude,
        longitude
    ) => {
        if (status) {
            status.textContent =
                `Selected point: ${latitude.toFixed(7)}, ${longitude.toFixed(7)}`;
        }
    };

    const syncInputs = (
        latitude,
        longitude
    ) => {
        syncing = true;

        latitudeInput.value =
            latitude.toFixed(7);

        longitudeInput.value =
            longitude.toFixed(7);

        syncing = false;

        setStatus(
            latitude,
            longitude
        );
    };

    const placeMarker = (
        latitude,
        longitude,
        center = false
    ) => {
        const point = L.latLng(
            latitude,
            longitude
        );

        if (!marker) {
            marker = L.marker(
                point,
                {
                    draggable: true,
                    icon: junctionIcon,
                }
            ).addTo(map);

            marker.on(
                'dragend',
                () => {
                    const position =
                        marker.getLatLng();

                    syncInputs(
                        position.lat,
                        position.lng
                    );
                }
            );
        } else {
            marker.setLatLng(point);
        }

        if (center) {
            map.setView(
                point,
                Math.max(
                    map.getZoom(),
                    17
                ),
                {
                    animate: true,
                }
            );
        }

        setStatus(
            latitude,
            longitude
        );
    };

    map.on(
        'click',
        (event) => {
            placeMarker(
                event.latlng.lat,
                event.latlng.lng
            );

            syncInputs(
                event.latlng.lat,
                event.latlng.lng
            );
        }
    );

    const syncFromInputs = () => {
        if (syncing) {
            return;
        }

        const latitude =
            parseCoordinate(
                latitudeInput.value
            );

        const longitude =
            parseCoordinate(
                longitudeInput.value
            );

        if (
            latitude === null
            || longitude === null
        ) {
            return;
        }

        placeMarker(
            latitude,
            longitude,
            true
        );
    };

    latitudeInput.addEventListener(
        'input',
        syncFromInputs
    );

    longitudeInput.addEventListener(
        'input',
        syncFromInputs
    );

    if (
        savedLatitude !== null
        && savedLongitude !== null
    ) {
        placeMarker(
            savedLatitude,
            savedLongitude,
            true
        );
    }

    window.setTimeout(
        () => map.invalidateSize(),
        100
    );
}

function initializeAll() {
    document
        .querySelectorAll(SELECTOR)
        .forEach(
            initializePicker
        );
}

if (
    document.readyState
    === 'loading'
) {
    document.addEventListener(
        'DOMContentLoaded',
        initializeAll,
        {
            once: true,
        }
    );
} else {
    initializeAll();
}

document.addEventListener(
    'livewire:navigated',
    initializeAll
);
